import { useQueryClient } from '@tanstack/react-query'
import * as turf from '@turf/turf'
import type { Feature } from 'geojson'
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { v4 as uuidV4 } from 'uuid'
import AppContext from '../../../context/appContext'
import { useExclusionAreas } from '../../../hooks/data'
import { queryKeys } from '../../../hooks/data/queryKeys'
import { ExclusionAreaGeoJSON } from '../../../types/apiModels'
import { GeoJSON } from '../../../utils/GeoJSON/index'
import {
  checkIntersectionWithOtherFeatures,
  checkIntersectionWithPerimeter,
  processIntersectionWithPerimeter,
} from '../../../utils/mapDrawHandle'
import { getFeatureId } from '../utils'
import { EditAction, EditState, editStateReducer, editStateSelectors } from './editStateReducer'

type MapAction = 'draw' | 'edit' | null

interface ExclusionAreaContextType {
  // State
  exclusionAreas: ExclusionAreaGeoJSON[]
  importedExclusionAreas: ExclusionAreaGeoJSON[]
  isLoading: boolean
  canEdit: boolean
  canEditGeometry: boolean
  editState: EditState
  editingItemId: string | null
  mapAction: MapAction
  selectedArea: ExclusionAreaGeoJSON | null
  originalArea: ExclusionAreaGeoJSON | null
  isDrawingMode: boolean
  isEditMode: boolean

  // Actions - Edit State Management
  startDrawing: () => void
  startDrawingOnSelected: () => void
  stopDrawingOnSelected: () => void
  startEditing: (editingItemId: string, area: ExclusionAreaGeoJSON) => void
  startEditingGeometry: (editingItemId: string, area: ExclusionAreaGeoJSON) => void
  cancelEdit: () => void
  finishEdit: (savedId?: string) => void

  // Actions - Geometry Operations (Map operations - create, union, edit vertices)
  handlePolygonDrawn: (feature: Feature) => void
  updateAreaGeometry: (feature: Feature) => void
  deletePolygonFromArea: (updatedFeature: Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>) => void

  // Actions - Property Updates (Panel operations - update name, type, etc.)
  updateSelectedArea: (area: ExclusionAreaGeoJSON) => void

  // Actions - UI Operations (Panel operations - unsaved changes and checkbox selection)
  setExclusionAreas: Dispatch<SetStateAction<ExclusionAreaGeoJSON[]>>
  setImportedExclusionAreas: Dispatch<SetStateAction<ExclusionAreaGeoJSON[]>>
  setSelectedExclusionAreaIds: (ids: Iterable<string>) => void

  // Data Operations
  reloadExclusionAreas: () => Promise<void>

  // Visibility
  isExclusionVisible: boolean
  setExclusionVisible: Dispatch<SetStateAction<boolean>>

  // Internal - only for advanced use cases
  dispatchEditAction: Dispatch<EditAction>
}

const ExclusionAreaContext = createContext<ExclusionAreaContextType>({
  exclusionAreas: [],
  importedExclusionAreas: [],
  isLoading: false,
  canEdit: false,
  canEditGeometry: false,
  editState: { mode: 'idle' },
  editingItemId: null,
  mapAction: null,
  selectedArea: null,
  originalArea: null,
  isDrawingMode: false,
  isEditMode: false,
  startDrawing: () => {},
  startDrawingOnSelected: () => {},
  stopDrawingOnSelected: () => {},
  startEditing: () => {},
  startEditingGeometry: () => {},
  cancelEdit: () => {},
  finishEdit: () => {},
  handlePolygonDrawn: () => {},
  updateAreaGeometry: () => {},
  deletePolygonFromArea: () => {},
  updateSelectedArea: () => {},
  setExclusionAreas: () => {},
  setImportedExclusionAreas: () => {},
  setSelectedExclusionAreaIds: () => {},
  reloadExclusionAreas: async () => {},
  isExclusionVisible: true,
  setExclusionVisible: () => {},
  dispatchEditAction: () => {},
})

type OverlayLayer = {
  entries: ReadonlyMap<string, ExclusionAreaGeoJSON>
  removedIds: ReadonlySet<string>
}

type Overlay = OverlayLayer & {
  farmId: string | undefined
  // Saved work whose post-save refetch failed: shown under later edits until newer server data
  // (dataUpdatedAt past releaseAfter) replaces it.
  held?: OverlayLayer & { releaseAfter: number }
}

type Selection = { farmId: string | undefined; ids: ReadonlySet<string> }

// Module scope so an idle provider keeps one identity per value and the derived lists' memos hold.
const NO_AREAS: ExclusionAreaGeoJSON[] = []
const NO_IDS: ReadonlySet<string> = new Set()
const EMPTY_OVERLAY: Overlay = { farmId: undefined, entries: new Map(), removedIds: NO_IDS }

const scopeOverlay = (overlay: Overlay, farmId: string | undefined): Overlay =>
  overlay.farmId === farmId ? overlay : EMPTY_OVERLAY

const liveHeld = ({ held }: Overlay, dataUpdatedAt: number) =>
  held && dataUpdatedAt <= held.releaseAfter ? held : undefined

// Entries without a base match (drafts, or an area deleted elsewhere mid-edit) follow the base list
// so the edit session keeps its item and its Cancel.
const mergeOverlay = (
  baseAreas: ExclusionAreaGeoJSON[],
  { entries, removedIds }: OverlayLayer,
): ExclusionAreaGeoJSON[] => {
  const baseIds = new Set(baseAreas.map(getFeatureId))
  return [
    ...baseAreas
      .filter(area => !removedIds.has(getFeatureId(area)))
      .map(area => entries.get(getFeatureId(area)) ?? area),
    ...[...entries].filter(([id]) => !baseIds.has(id)).map(([, area]) => area),
  ]
}

// A held draft must carry the id the server gave it, or a later save would create it a second time.
const withServerId = (area: ExclusionAreaGeoJSON, id: string): ExclusionAreaGeoJSON => {
  const { tmpId: _tmpId, ...properties } = area.properties
  return { ...area, properties: { ...properties, id } }
}

const withHeld = (serverAreas: ExclusionAreaGeoJSON[], held: OverlayLayer | undefined) =>
  held ? mergeOverlay(serverAreas, held) : serverAreas

// `selected` is display state projected from the selection set; it must never reach an entry, or it
// would pin the highlight against unchecking.
const withoutSelected = (area: ExclusionAreaGeoJSON): ExclusionAreaGeoJSON => {
  if (!('selected' in area.properties)) return area
  const { selected: _selected, ...properties } = area.properties
  return { ...area, properties }
}

// Every updater keeps the features it does not change by reference (map/filter, and the draw
// clipping pushes untouched neighbours as is), and a selection copy only adds `selected`; anything
// else is an unsaved change.
const isUnchanged = (area: ExclusionAreaGeoJSON, baseArea: ExclusionAreaGeoJSON | undefined) => {
  if (area === baseArea) return true
  if (!baseArea || area.geometry !== baseArea.geometry) return false
  const { properties } = withoutSelected(area)
  const keys = new Set([...Object.keys(properties), ...Object.keys(baseArea.properties)])
  return [...keys].every(key => properties[key] === baseArea.properties[key])
}

const toLayer = (
  areas: ExclusionAreaGeoJSON[],
  baseAreas: ExclusionAreaGeoJSON[],
): OverlayLayer => {
  const baseById = new Map(baseAreas.map(area => [getFeatureId(area), area] as const))
  const keptIds = new Set(areas.map(getFeatureId))
  return {
    entries: new Map(
      areas
        .filter(area => !isUnchanged(area, baseById.get(getFeatureId(area))))
        .map(area => [getFeatureId(area), withoutSelected(area)] as const),
    ),
    removedIds: new Set([...baseById.keys()].filter(id => !keptIds.has(id))),
  }
}

export const useExclusionAreaContext = () => {
  const context = useContext(ExclusionAreaContext)
  if (!context) {
    throw new Error('useExclusionAreaContext must be used within a ExclusionAreaProvider')
  }
  return context
}

interface ExclusionAreaProviderProps {
  canEdit: boolean
}

export const ExclusionAreaProvider: FC<PropsWithChildren<ExclusionAreaProviderProps>> = ({
  children,
  canEdit,
}) => {
  const { currentFarm, setLoadingModalConfig, setPartialChanges } = useContext(AppContext)
  const farmId = currentFarm?.id
  const queryClient = useQueryClient()
  // Only the initial load counts as loading: the blocking modal, the map spinner and the panel
  // skeletons would otherwise interrupt drawing and editing on every background refetch.
  const { data, isLoading, dataUpdatedAt } = useExclusionAreas({ farmId })
  const serverAreas = data ?? NO_AREAS

  // Drafts, vertex edits and the neighbours a draw clipped or swallowed stay out of the query
  // cache, layered over the server list, so a refetch updates the saved areas without discarding
  // unsaved work. Scoped to the farm because the provider outlives a farm switch.
  const [overlayState, setOverlayState] = useState<Overlay>(EMPTY_OVERLAY)
  const [selection, setSelection] = useState<Selection>({ farmId, ids: NO_IDS })
  const overlay = scopeOverlay(overlayState, farmId)
  const held = liveHeld(overlay, dataUpdatedAt)
  const selectedIds = selection.farmId === farmId ? selection.ids : NO_IDS

  const baseAreas = useMemo(() => withHeld(serverAreas, held), [serverAreas, held])
  const mergedAreas = useMemo(() => mergeOverlay(baseAreas, overlay), [baseAreas, overlay])

  // Projected last, on a copy: the selection survives refetches without freezing the areas.
  const exclusionAreas = useMemo(
    () =>
      mergedAreas.map(area =>
        selectedIds.has(getFeatureId(area))
          ? { ...area, properties: { ...area.properties, selected: true } }
          : area,
      ),
    [mergedAreas, selectedIds],
  )

  // Refs instead of a functional setState: callers' updaters dispatch edit actions, so each must
  // run exactly once and outside React's state updaters; writes in one event still compose.
  const overlayRef = useRef(EMPTY_OVERLAY)
  const serverRef = useRef({ farmId, serverAreas, dataUpdatedAt })
  // Layout, not passive: children's effects run before the provider's, and one that writes in
  // reaction to fresh data must diff against that data.
  useLayoutEffect(() => {
    serverRef.current = { farmId, serverAreas, dataUpdatedAt }
  }, [farmId, serverAreas, dataUpdatedAt])

  const writeOverlay = useCallback((next: Overlay) => {
    overlayRef.current = next
    setOverlayState(next)
  }, [])

  const readLatest = useCallback(() => {
    const latest = serverRef.current
    const current = scopeOverlay(overlayRef.current, latest.farmId)
    const latestHeld = liveHeld(current, latest.dataUpdatedAt)
    return {
      ...latest,
      current,
      held: latestHeld,
      baseAreas: withHeld(latest.serverAreas, latestHeld),
    }
  }, [])

  // Ending an edit drops only its own changes: held saved work waits for newer server data.
  const clearEdits = useCallback(() => {
    const latest = readLatest()
    writeOverlay(
      latest.held ? { ...EMPTY_OVERLAY, farmId: latest.farmId, held: latest.held } : EMPTY_OVERLAY,
    )
  }, [readLatest, writeOverlay])

  const setExclusionAreas = useCallback(
    (update: SetStateAction<ExclusionAreaGeoJSON[]>) => {
      const latest = readLatest()
      const next =
        typeof update === 'function'
          ? update(mergeOverlay(latest.baseAreas, latest.current))
          : update
      writeOverlay({ farmId: latest.farmId, held: latest.held, ...toLayer(next, latest.baseAreas) })
    },
    [readLatest, writeOverlay],
  )

  const setSelectedExclusionAreaIds = useCallback(
    (ids: Iterable<string>) => setSelection({ farmId, ids: new Set(ids) }),
    [farmId],
  )

  // The ref guard keeps the provider from closing a modal some other component opened.
  const openedModalRef = useRef(false)
  useEffect(() => {
    if (isLoading) {
      openedModalRef.current = true
      setLoadingModalConfig({
        open: true,
        title: 'Cargando áreas de exclusión...',
        dialogMessage: 'Cargando áreas de exclusión...',
      })
    } else if (openedModalRef.current) {
      openedModalRef.current = false
      setLoadingModalConfig({ open: false })
    }
  }, [isLoading, setLoadingModalConfig])

  // Unmounting mid-load would strand the dismissless global modal open: the isLoading→false
  // transition never reaches an unmounted provider.
  useEffect(
    () => () => {
      if (openedModalRef.current) {
        openedModalRef.current = false
        setLoadingModalConfig({ open: false })
      }
    },
    [setLoadingModalConfig],
  )

  const [importedExclusionAreas, setImportedExclusionAreas] = useState<ExclusionAreaGeoJSON[]>([])
  const [isExclusionVisible, setExclusionVisible] = useState(true)

  const [editState, dispatchEditAction] = useReducer(editStateReducer, { mode: 'idle' })

  const editStateRef = useRef(editState)
  useEffect(() => {
    editStateRef.current = editState
  }, [editState])

  const editingItemId = editStateSelectors.getEditingItemId(editState)
  const selectedArea = editStateSelectors.getSelectedArea(editState)
  const originalArea = editStateSelectors.getOriginalArea(editState)
  const mapAction = editStateSelectors.getMapAction(editState)
  const isDrawingMode = editStateSelectors.isDrawing(editState)
  const isEditMode = editStateSelectors.isEditing(editState)
  const canEditGeometry = editStateSelectors.canEditGeometry(editState)

  const startDrawing = useCallback(() => {
    dispatchEditAction({ type: 'START_DRAWING' })
  }, [])

  const startDrawingOnSelected = useCallback(() => {
    dispatchEditAction({ type: 'START_DRAWING_ON_SELECTED' })
  }, [])

  const stopDrawingOnSelected = useCallback(() => {
    dispatchEditAction({ type: 'STOP_DRAWING_ON_SELECTED' })
  }, [])

  const startEditing = useCallback(
    (itemId: string, area: ExclusionAreaGeoJSON) => {
      dispatchEditAction({
        type: 'START_EDITING',
        payload: { editingItemId: itemId, area },
      })
      setPartialChanges(true)
    },
    [setPartialChanges],
  )

  const startEditingGeometry = useCallback(
    (itemId: string, area: ExclusionAreaGeoJSON) => {
      dispatchEditAction({
        type: 'START_EDITING_GEOMETRY',
        payload: { editingItemId: itemId, area },
      })
      setPartialChanges(true)
    },
    [setPartialChanges],
  )

  const cancelEdit = useCallback(() => {
    dispatchEditAction({ type: 'CANCEL_EDIT' })
    clearEdits()
    setPartialChanges(false)
  }, [clearEdits, setPartialChanges])

  // A save finishes after its refetch settled, but that refetch can have failed (invalidateQueries
  // swallows it): then the saved work is held until newer server data replaces it.
  const finishEdit = useCallback(
    (savedId?: string) => {
      const editedId = editStateSelectors.getEditingItemId(editStateRef.current)
      dispatchEditAction({ type: 'FINISH_EDIT' })
      const query = queryClient.getQueryState(queryKeys.exclusionAreas(farmId))
      if (query?.status === 'error') {
        const latest = readLatest()
        const saved = mergeOverlay(latest.baseAreas, latest.current).map(area =>
          savedId && getFeatureId(area) === editedId ? withServerId(area, savedId) : area,
        )
        writeOverlay({
          ...EMPTY_OVERLAY,
          farmId: latest.farmId,
          held: { ...toLayer(saved, latest.serverAreas), releaseAfter: query.dataUpdatedAt },
        })
      } else {
        writeOverlay(EMPTY_OVERLAY)
      }
      setPartialChanges(false)
    },
    [farmId, queryClient, readLatest, setPartialChanges, writeOverlay],
  )

  const updateSelectedArea = useCallback(
    (area: ExclusionAreaGeoJSON) => {
      if ((area.properties as any).isImported) {
        setImportedExclusionAreas(prev =>
          prev.map(a => (getFeatureId(a) === getFeatureId(area) ? area : a)),
        )
      } else {
        setExclusionAreas(
          prev => prev?.map(a => (getFeatureId(a) === getFeatureId(area) ? area : a)) || [],
        )
      }

      dispatchEditAction({
        type: 'UPDATE_SELECTED_AREA',
        payload: { area },
      })
      setPartialChanges(true)
    },
    [setExclusionAreas, setPartialChanges],
  )

  const handlePolygonDrawn = useCallback(
    (feature: Feature) => {
      if (feature.geometry.type !== 'Polygon') {
        console.warn('Can only handle Polygon features')
        return
      }

      const currentEditState = editStateRef.current
      const currentSelectedArea = editStateSelectors.getSelectedArea(currentEditState)
      const currentIsEditMode = editStateSelectors.isEditing(currentEditState)
      const currentCanEditGeometry = editStateSelectors.canEditGeometry(currentEditState)

      if (currentSelectedArea && currentIsEditMode) {
        // Don't allow geometry updates if not in geometry editing mode
        if (!currentCanEditGeometry) return

        const polygonFeature = feature as Feature<GeoJSON.Polygon>
        let union = turf.union(polygonFeature, currentSelectedArea)

        setExclusionAreas(prev => {
          const i = prev.findIndex(area => getFeatureId(area) === getFeatureId(currentSelectedArea))

          union = checkIntersectionWithPerimeter(union, currentFarm?.toGeoJSON)

          const otherExclusionAreas = prev.filter((_, index) => index !== i)
          const newFeatures = checkIntersectionWithOtherFeatures(union, otherExclusionAreas)

          union.properties = currentSelectedArea.properties
          union.properties.area = GeoJSON.hectareArea(union)

          newFeatures.splice(i, 0, union)

          dispatchEditAction({
            type: 'UPDATE_SELECTED_AREA',
            payload: { area: union as ExclusionAreaGeoJSON },
          })

          return newFeatures
        })

        stopDrawingOnSelected()
        setPartialChanges(true)
      } else {
        let newArea = turf.polygon(feature.geometry.coordinates)
        newArea = processIntersectionWithPerimeter(newArea, currentFarm?.toGeoJSON)

        if (!newArea) {
          console.warn('Exclusion area is outside the farm perimeter')
          cancelEdit()
          return
        }

        const newExclusionArea: ExclusionAreaGeoJSON = {
          type: 'Feature',
          geometry: newArea.geometry as ExclusionAreaGeoJSON['geometry'],
          properties: {
            id: undefined,
            name: '',
            farmId: currentFarm.id,
            area: GeoJSON.hectareArea(newArea),
            color: 'black',
            type: 'exclusion_area',
            exclusionAreaTypeId: null,
            hasGrazingManagement: false,
            tmpId: uuidV4(),
          },
        }

        setExclusionAreas(prev => {
          const newFeatures = checkIntersectionWithOtherFeatures(newArea, prev || [])
          return [...newFeatures, newExclusionArea]
        })

        startEditingGeometry(newExclusionArea.properties.tmpId!, newExclusionArea)
      }
    },
    [
      currentFarm,
      setExclusionAreas,
      stopDrawingOnSelected,
      setPartialChanges,
      startEditingGeometry,
      cancelEdit,
    ],
  )

  const updateAreaGeometry = useCallback(
    (feature: Feature) => {
      if (!selectedArea) return
      // Check state directly via ref to ensure freshness if needed, but selectedArea dependency implies strict update
      const currentEditState = editStateRef.current
      if (!editStateSelectors.canEditGeometry(currentEditState)) return

      const updatedArea: ExclusionAreaGeoJSON = {
        ...selectedArea,
        geometry: feature.geometry as ExclusionAreaGeoJSON['geometry'],
        properties: {
          ...selectedArea.properties,
          area: GeoJSON.hectareArea(feature),
        },
      }

      setExclusionAreas(
        prev =>
          prev?.map(area =>
            getFeatureId(area) === getFeatureId(selectedArea) ? updatedArea : area,
          ) || [],
      )

      dispatchEditAction({
        type: 'UPDATE_SELECTED_AREA',
        payload: { area: updatedArea },
      })
      setPartialChanges(true)
    },
    [selectedArea, setExclusionAreas, setPartialChanges],
  )

  const deletePolygonFromArea = useCallback(
    (updatedFeature: Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>) => {
      if (!selectedArea) return
      const currentEditState = editStateRef.current
      if (!editStateSelectors.canEditGeometry(currentEditState)) return

      const updatedExclusionArea: ExclusionAreaGeoJSON = {
        type: 'Feature',
        geometry: {
          type: updatedFeature.geometry.type,
          coordinates: updatedFeature.geometry.coordinates,
          crs: selectedArea.geometry.crs,
        } as ExclusionAreaGeoJSON['geometry'],
        properties: {
          ...selectedArea.properties,
          area: GeoJSON.hectareArea(updatedFeature),
        },
      }

      setExclusionAreas(
        prev =>
          prev?.map(area =>
            getFeatureId(area) === getFeatureId(selectedArea) ? updatedExclusionArea : area,
          ) || [],
      )

      dispatchEditAction({
        type: 'UPDATE_SELECTED_AREA',
        payload: { area: updatedExclusionArea },
      })
      setPartialChanges(true)
    },
    [selectedArea, setExclusionAreas, setPartialChanges],
  )

  const reloadExclusionAreas = useCallback(async () => {
    dispatchEditAction({ type: 'RESET' })
    clearEdits()
    await queryClient.invalidateQueries({ queryKey: queryKeys.exclusionAreas(farmId) })
  }, [clearEdits, queryClient, farmId])

  const contextValue = useMemo(
    () => ({
      exclusionAreas,
      importedExclusionAreas,
      isLoading,
      canEdit,
      canEditGeometry,
      editState,
      editingItemId,
      mapAction,
      selectedArea,
      originalArea,
      isDrawingMode,
      isEditMode,
      startDrawing,
      startDrawingOnSelected,
      stopDrawingOnSelected,
      startEditing,
      startEditingGeometry,
      cancelEdit,
      finishEdit,
      handlePolygonDrawn,
      updateAreaGeometry,
      deletePolygonFromArea,
      updateSelectedArea,
      setExclusionAreas,
      setImportedExclusionAreas,
      setSelectedExclusionAreaIds,
      reloadExclusionAreas,
      isExclusionVisible,
      setExclusionVisible,
      dispatchEditAction,
    }),
    [
      exclusionAreas,
      importedExclusionAreas,
      isLoading,
      canEdit,
      canEditGeometry,
      editState,
      editingItemId,
      mapAction,
      selectedArea,
      originalArea,
      isDrawingMode,
      isEditMode,
      startDrawing,
      startDrawingOnSelected,
      stopDrawingOnSelected,
      startEditing,
      startEditingGeometry,
      cancelEdit,
      finishEdit,
      handlePolygonDrawn,
      updateAreaGeometry,
      deletePolygonFromArea,
      updateSelectedArea,
      setExclusionAreas,
      setImportedExclusionAreas,
      setSelectedExclusionAreaIds,
      reloadExclusionAreas,
      isExclusionVisible,
    ],
  )

  return (
    <ExclusionAreaContext.Provider value={contextValue}>{children}</ExclusionAreaContext.Provider>
  )
}
